<script setup lang="ts">
import { login } from "~/instances/KeurtApiClient";
import type { User } from "../models/User";
import { AccessType } from "~/enums/accesstypes";
import InputText from "primevue/inputtext";
import Button from "primevue/button"; // Ensure Button is imported

const email = ref();
const password = ref();
const submitted = ref<boolean>(false);
const loginSucces = ref<boolean>(true);
const emailErrorMessage = ref<string>();
const passwordErrorMessage = ref<string>();
const loading = ref<boolean>(false); // New loading state

const tryLogin = async () => {
  submitted.value = true;
  if (!email.value) {
    emailErrorMessage.value = "verplicht veld";
    return;
  }
  if (!password.value) {
    passwordErrorMessage.value = "verplicht veld";
    return;
  }
  if (!validateEmail(email.value)) {
    emailErrorMessage.value = "E-mailadres is niet correct";
    return;
  }

  loading.value = true; // Start loading spinner
  try {
    loginSucces.value = await login(email.value, password.value);
    if (loginSucces.value) {
      navigateBasedOnAccesstype();
    } else {
      passwordErrorMessage.value = "Wachtwoord incorrect";
    }
  } catch (error) {
    console.error("Login failed:", error);
  } finally {
    loading.value = false; // Stop loading spinner
  }
};

const validateEmail = (email: string) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

const navigateBasedOnAccesstype = () => {
  const userData = sessionStorage.getItem("user");
  if (userData) {
    const user: User = JSON.parse(userData);
    switch (user.accessType) {
      case AccessType.Admin:
      case AccessType.Keurmeister:
        navigateTo("/");
        break;
      case AccessType.CustUser:
      case AccessType.UserAdmin:
      case AccessType.custProductMover:
        navigateTo("/locations");
        break;
      case AccessType.Transporter:
        navigateTo("/transporter");
        break;
    }
  } else {
    return navigateTo("/login");
  }
};
</script>

<template>
  <div class="grid min-h-screen place-content-center bg-app-background">
    <div class="flex flex-col justify-content w-80">
      <div class="theme-logo bg-center flex justify-center h-[70px] w-[160px] mx-auto mb-8" />
      <div class="flex flex-col">
        <label for="email" class="text-sm text-[#556D85]">E-mailadres</label>
        <InputText
          id="email"
          v-model="email"
          type="email"
          name="email"
          class="rounded-full"
          :class="{
            'p-invalid': submitted && (!email || !validateEmail(email)),
          }"
          placeholder="gebruiker@domein.nl"
          @keyup.enter="tryLogin()"
        />
        <small
          v-if="submitted && (!email || !validateEmail(email))"
          class="p-error"
          >{{ emailErrorMessage }}</small
        >
      </div>
      <div class="flex flex-col mt-9 mb-6">
        <label for="password" class="text-sm text-[#556D85]">Wachtwoord</label>
        <InputText
          id="password"
          v-model="password"
          type="password"
          name="password"
          :class="{ 'p-invalid': submitted && (!password || !loginSucces) }"
          class="rounded-full"
          @keyup.enter="tryLogin()"
        />
        <small
          v-if="submitted && (!password || !loginSucces)"
          class="p-error"
          >{{ passwordErrorMessage }}</small
        >
      </div>

      <Button
        class="w-80"
        label="Inloggen"
        rounded
        :loading="loading"
        @click="tryLogin()"
      />
    </div>
    <a class="flex justify-evenly mt-4" href="/requestPasswordReset"
      ><p class="text-[#FF6828]">Wachtwoord vergeten?</p></a
    >
  </div>
</template>
